import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import BrandLayout from "../layouts/brand/BrandLayout";
import PricingLayout from "../layouts/brand/PricingLayout";
import { BrandProtectedRoute } from "../components/auth/BrandProtectedRoute";
import { BrandAuthProvider } from "../contexts/BrandAuthContext/provider";

// Brand Pages
import { default as BrandDashboard } from "../pages/brand/Dashboard";
import { ActiveAffiliates } from "../pages/brand/affiliates/ActiveAffiliates";
import { InvitedAffiliates } from "../pages/brand/affiliates/InvitedAffiliates";
import { AffiliateDetail } from "../pages/brand/affiliates/AffiliateDetail";
import { ProductsList } from "../pages/brand/ProductsList";
import { ProductsInsights } from "../pages/brand/ProductsInsights";
import { ProductsAnalytics } from "../pages/brand/products/ProductsAnalytics";
import { ProgramsAnalytics } from "../pages/brand/programs/ProgramsAnalytics";
import { PendingPayouts } from "../pages/brand/payouts/PendingPayouts";
import { PayoutHistory } from "../pages/brand/payouts/PayoutHistory";
import { PayoutDetail } from "../pages/brand/payouts/PayoutDetail";
import { Compliance } from "../pages/brand/payouts/Compliance";
import Settings from "../pages/brand/Settings/Settings";
import ProgramPage from "../pages/brand/ProgramPage";
import OAuthConsent from "../pages/brand/OAuthConsent";
import { Support } from "../pages/brand/support/Support";
import { InviteAffiliates } from "../pages/brand/affiliates/InviteAffiliates";
import { BrandDetail } from "../pages/brand/BrandDetail";
import { Applicants } from "../pages/brand/affiliates/Applicants";
import { Payouts } from "../pages/brand/payouts/Payouts";
import Campaigns from "../pages/brand/Campaigns";
import CampaignFormPage from "../pages/brand/CampaignForm";
import { BrandPricing } from "../pages/brand/BrandPricing";
import { AgencyRequests } from "../pages/brand/AgencyRequests";
import EmailAutomation from "../pages/brand/automations/EmailAutomation";
import CustomDiscounts from "../pages/brand/programs/CustomDiscounts";
import CreateProgram from "../pages/brand/programs/CreateProgram";
import SocialSearch from "../pages/brand/AiSearch/SocialSearch";
import TikTokShopPage from "../pages/brand/tiktok/TikTokShopPage";
import ProgramsList from "../pages/brand/programs/ProgramsList";
import ProgramEdit from "../pages/brand/programs/ProgramEdit";

// Auth Pages
import { Login } from "../pages/auth/Login";
import { BrandSignUp } from "../pages/brand/auth/SignUp";
import { InstallApp } from "../pages/brand/auth/InstallApp";

import { BrandVerifyOtp } from "../pages/brand/auth/VerifyOtp";
import { BrandResetPassword } from "../pages/brand/auth/ResetPassword";
import { NotFound } from "../pages/NotFound";
import { AuthLayout } from "@/pages/auth/Layout";
import { AuthProvider } from "@/contexts/AuthContext";
import { PermissionsProvider } from "../contexts/PermissionsContext";
import Reauthorize from "@/pages/brand/reauthorize";
import Faq from "@/pages/Faq";
import RewardsLanding from "@/components/common/rewardLanding";
import ReveshareResources from "@/pages/brand/ReveshareResources";
import PostCheckout from "@/pages/brand/PostCheckout/PostCheckout";
import PostCheckoutSales from "@/pages/brand/PostCheckoutSales";
import { ShareResources } from "../pages/brand/Settings/tabs/ShareResources";
import { AppearancePreview } from "../pages/brand/AppearancePreview";
import { SimplifiedPreview } from "../pages/brand/SimplifiedPreview";
import { BrandForgotPassword } from "@/pages/brand/auth/ForgotPassword";
import { SignupPreviewProvider } from "@/contexts/SignupPreviewContext";
import TestAccounts from "../pages/brand/affiliates/TestAccounts";
import MetaAdsPage from "../pages/brand/meta-ads/MetaAdsPage";
import AdPerformancePage from "../pages/brand/meta-ads/performance/AdPerformancePage";
import Integrations from "../pages/brand/integrations/Integrations";
import RechargePage from "../pages/brand/integrations/RechargePage";
import BrandContentProgramsList from "../pages/brand/content-programs/ContentProgramsList";
import BrandCreateContentProgram from "../pages/brand/content-programs/CreateContentProgram";
import BrandContentProgramDetail from "../pages/brand/content-programs/ContentProgramDetail";
import ReveshareAmpedSignup from "../pages/brand/ReveshareAmpedSignup";
import ReveshareAmpedPricing from "../pages/brand/ReveshareAmpedPricing";
import OnboardingDashboard from "../pages/brand/onboarding/OnboardingDashboard";
import { SHOW_ONBOARDING } from "@/config/features";

export function BrandRoutes() {
  const location = useLocation();
  return (
    <AuthProvider>
      <BrandAuthProvider>
        <PermissionsProvider>
        <SignupPreviewProvider>
          <Routes key={location.pathname}>
            {/* Brand Auth Routes */}

            <Route key={location.pathname} path="auth" element={<AuthLayout />}>
              <Route index element={<InstallApp />} />
              <Route path="login" element={<Login />} />
              <Route path="signup" element={<BrandSignUp />} />
              <Route path="forgot-password" element={<BrandForgotPassword />} />
              <Route path="verify-otp" element={<BrandVerifyOtp />} />
              <Route path="reset-password" element={<BrandResetPassword />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Brand Pricing Route - Outside protected routes */}
            <Route path="pricing" element={<PricingLayout />}>
              <Route index element={<BrandPricing />} />
            </Route>

            {/* Reveshare Amped Pricing - Outside protected layout so it renders full-page */}
            <Route
              path="reveshare-amped/pricing"
              element={
                <BrandProtectedRoute>
                  <ReveshareAmpedPricing />
                </BrandProtectedRoute>
              }
            />

            {/* Appearance Preview Route - Protected but without layout wrapper for iframe */}
            <Route
              path="appearance-preview/:username"
              element={
                <BrandProtectedRoute>
                  <AppearancePreview />
                </BrandProtectedRoute>
              }
            />

            {/* Simplified Preview Route - For signup preview (no auth required) */}
            <Route path="simplified-preview" element={<SimplifiedPreview />} />

            {/* Protected Brand Routes */}
            <Route key={location.pathname}
              element={
                <BrandProtectedRoute>
                  <BrandLayout />
                </BrandProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" index element={<BrandDashboard />} />
              <Route
                path="onboarding"
                element={SHOW_ONBOARDING ? <OnboardingDashboard /> : <NotFound />}
              />
              <Route path="rewards" index element={<RewardsLanding />} />
              <Route path="affiliates-analytics" element={<BrandDashboard />} />

              <Route path="products">
                <Route index element={<Navigate to="list" replace />} />
                <Route path="list" element={<ProductsList />} />
                <Route path="analytics" element={<ProductsAnalytics />} />
                <Route
                  path="insights/:productId"
                  element={<ProductsInsights />}
                />
              </Route>

              {/* Campaigns */}
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<CampaignFormPage />} />
              <Route
                path="campaigns/:campaignId/edit"
                element={<CampaignFormPage />}
              />
              <Route path="reauthorize" element={<Reauthorize />} />

              {/* Meta Ads */}
              <Route path="meta-ads" element={<MetaAdsPage />} />
              <Route path="meta-ads/performance" element={<AdPerformancePage />} />

              {/* Social Search */}
              <Route path="social-search" element={<SocialSearch />} />
              <Route path="tiktok" element={<TikTokShopPage />} />
              <Route
                path="ai-search"
                element={<Navigate to="../social-search" replace />}
              />

              <Route path="content-programs">
                <Route index element={<BrandContentProgramsList />} />
                <Route path="new" element={<BrandCreateContentProgram />} />
                <Route
                  path=":programId/edit"
                  element={<BrandCreateContentProgram />}
                />
                <Route
                  path=":programId"
                  element={<BrandContentProgramDetail />}
                /> 
              </Route>
               

              {/* Partner Management */}
              <Route path="agency-requests" element={<AgencyRequests />} />

              {/* Affiliate Management */}
              <Route path="affiliates">
                <Route index element={<Navigate to="active" replace />} />
                <Route path="active" element={<ActiveAffiliates />} />
                <Route path="invited" element={<InvitedAffiliates />} />
                <Route path="invite" element={<InviteAffiliates />} />
                <Route path="applicants" element={<Applicants />} />
                <Route path="share-resources" element={<ShareResources />} />
                <Route path="test-accounts" element={<TestAccounts />} />
                <Route path=":id" element={<AffiliateDetail />} />
              </Route>

              {/* Payout Management */}
              <Route path="payouts">
                <Route index element={<Payouts />} />
                <Route path="pending" element={<PendingPayouts />} />
                <Route path="history" element={<PayoutHistory />} />
                <Route path="history/:id" element={<PayoutDetail />} />
                <Route path="compliance" element={<Compliance />} />
              </Route>

              <Route path="integrations">
                <Route index element={<Integrations />} />
                <Route path="recharge" element={<RechargePage />} />
              </Route>

              {/* Automation */}
              <Route path="automations">
                <Route index element={<Navigate to="email" replace />} />
                <Route path="email" element={<EmailAutomation />} />
              </Route>

              {/* Programs */}
              <Route path="programs">
                <Route index element={<ProgramsList />} />
                <Route path="analytics" element={<ProgramsAnalytics />} />
                <Route path=":programId" element={<ProgramEdit />} />
                <Route path="custom-discounts" element={<CustomDiscounts />} />
                <Route path="create" element={<CreateProgram />} />
              </Route>

              {/* Tier program page builder (white-label) */}
              <Route path="program-page" element={<ProgramPage />} />

              {/* Reveshare Resources */}
              <Route
                path="reveshare-resources"
                element={<ReveshareResources />}
              />

              {/* Post-checkout Affiliate Onboarding */}
              <Route path="post-checkout" element={<PostCheckout />} />
              <Route
                path="post-checkout-sales"
                element={<PostCheckoutSales />}
              />

              {/* Reveshare Amped Signup */}
              <Route path="reveshare-amped/signup" element={<ReveshareAmpedSignup />} />

              {/* Other Settings */}
              <Route path="settings" element={<Settings />} />
              {/* MCP connector consent (OAuth authorize round-trip) */}
              <Route path="oauth/consent" element={<OAuthConsent />} />
              <Route path="support" element={<Support />} />
              <Route path="detail/:id" element={<BrandDetail />} />
              <Route path="faq" element={<Faq />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </SignupPreviewProvider>
        </PermissionsProvider>
      </BrandAuthProvider>
    </AuthProvider>
  );
}
